import { useEffect, useMemo, useState } from "react";
import {
  getWatchlist,
  watchlistAdd,
  watchlistRemove,
  discoverRisersFallers,
  getQuote,
} from "@/lib/api";
import { formatPrice, friendlyName } from "@/lib/symbols";
import { Star, ChevronDown, ChevronRight } from "lucide-react";

/**
 * WatchlistPanel — shows the user's starred symbols + collapsible preset lists
 * (Top Risers / Top Fallers). Clicking a row loads the chart. Rows use
 * gold=up / metallic-purple=down.
 */
export default function WatchlistPanel({ onSelect, activeSymbol }) {
  const [symbols, setSymbols] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [rf, setRf] = useState(null);
  const [openRisers, setOpenRisers] = useState(true);
  const [openFallers, setOpenFallers] = useState(false);

  const refresh = async () => {
    try {
      const w = await getWatchlist();
      const list = w.symbols || [];
      setSymbols(list);
      // Quote each in parallel
      const results = await Promise.all(
        list.map((s) => getQuote(s).then((q) => [s, q]).catch(() => [s, null])),
      );
      const next = {};
      for (const [s, q] of results) next[s] = q;
      setQuotes(next);
    } catch {}
  };

  useEffect(() => {
    refresh();
    discoverRisersFallers(5).then(setRf).catch(() => {});
    const iv = setInterval(refresh, 60000);
    return () => clearInterval(iv);
  }, []);

  const remove = async (sym) => {
    try {
      const r = await watchlistRemove(sym);
      setSymbols(r.symbols || []);
    } catch {}
  };

  return (
    <div className="flex flex-col gap-3" data-testid="watchlist-panel">
      {/* My favourites */}
      <div>
        <div className="mb-1 flex items-baseline justify-between">
          <span className="font-heading text-[9px] tracking-[0.18em] text-[var(--aurion-muted)]">
            MY WATCHLIST
          </span>
          <span className="text-[9px] text-[var(--aurion-muted)]">
            {symbols.length}
          </span>
        </div>
        {symbols.length === 0 ? (
          <p
            className="text-[10px] text-[var(--aurion-muted)]"
            data-testid="watchlist-empty"
          >
            Star any symbol in the "/" search modal to pin it here.
          </p>
        ) : (
          <ul className="space-y-[1px]">
            {symbols.map((sym) => (
              <WatchRow
                key={sym}
                sym={sym}
                q={quotes[sym]}
                active={activeSymbol === sym}
                onSelect={onSelect}
                onUnstar={() => remove(sym)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Presets */}
      <PresetSection
        testid="watchlist-risers"
        title="TOP RISERS"
        rows={rf?.risers}
        open={openRisers}
        onToggle={() => setOpenRisers((v) => !v)}
        onSelect={onSelect}
        activeSymbol={activeSymbol}
      />
      <PresetSection
        testid="watchlist-fallers"
        title="TOP FALLERS"
        rows={rf?.fallers}
        open={openFallers}
        onToggle={() => setOpenFallers((v) => !v)}
        onSelect={onSelect}
        activeSymbol={activeSymbol}
      />
    </div>
  );
}

function WatchRow({ sym, q, active, onSelect, onUnstar }) {
  const price = q?.price;
  const chg = q?.change_pct;
  const isUp = chg == null ? null : chg >= 0;
  return (
    <li
      data-testid={`watchlist-row-${sym}`}
      className={`group flex cursor-pointer items-center justify-between border-l-2 px-1.5 py-1 text-[10px] transition-colors ${
        active
          ? "border-[var(--aurion-gold)] bg-[rgba(201,162,39,0.05)]"
          : "border-transparent hover:bg-white/[0.03]"
      }`}
      onClick={() => onSelect?.(sym)}
    >
      <div className="flex min-w-0 items-baseline gap-1.5">
        <button
          type="button"
          data-testid={`watchlist-unstar-${sym}`}
          onClick={(e) => { e.stopPropagation(); onUnstar?.(); }}
          className="text-[var(--aurion-gold)] opacity-70 hover:opacity-100"
          title="Remove from watchlist"
        >
          <Star className="h-3 w-3" fill="currentColor" />
        </button>
        <span
          className="font-heading tracking-widest text-[var(--aurion-text)] truncate"
          title={friendlyName(sym)}
        >
          {friendlyName(sym, sym)}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono tabular-nums text-[var(--aurion-text-dim)]">
          {price != null ? formatPrice(sym, price) : "—"}
        </span>
        {chg != null ? (
          <span className={`font-mono tabular-nums ${isUp ? "text-up" : "text-down"}`}>
            {isUp ? "+" : ""}
            {chg.toFixed(2)}%
          </span>
        ) : null}
      </div>
    </li>
  );
}

function PresetSection({ testid, title, rows, open, onToggle, onSelect, activeSymbol }) {
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="mb-1 flex w-full items-center gap-1 text-[9px] font-heading tracking-[0.18em] text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
        data-testid={`${testid}-toggle`}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </button>
      {open ? (
        !rows ? (
          <p className="text-[10px] text-[var(--aurion-muted)]">loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-[10px] text-[var(--aurion-muted)]">no data</p>
        ) : (
          <ul className="space-y-[1px]">
            {rows.map((r) => {
              const chg = r.day_change_pct;
              const isUp = chg == null ? null : chg >= 0;
              const active = activeSymbol === r.symbol;
              return (
                <li
                  key={r.symbol}
                  data-testid={`${testid}-row-${r.symbol}`}
                  className={`flex cursor-pointer items-center justify-between border-l-2 px-1.5 py-1 text-[10px] transition-colors ${
                    active
                      ? "border-[var(--aurion-gold)] bg-[rgba(201,162,39,0.05)]"
                      : "border-transparent hover:bg-white/[0.03]"
                  }`}
                  onClick={() => onSelect?.(r.symbol)}
                >
                  <span
                    className="min-w-0 truncate font-heading tracking-widest text-[var(--aurion-text)]"
                    title={friendlyName(r.symbol, r.name)}
                  >
                    {friendlyName(r.symbol, r.symbol)}
                  </span>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono tabular-nums text-[var(--aurion-text-dim)]">
                      {formatPrice(r.symbol, r.last_close)}
                    </span>
                    {chg != null ? (
                      <span
                        className={`font-mono tabular-nums ${isUp ? "text-up" : "text-down"}`}
                      >
                        {isUp ? "+" : ""}
                        {chg.toFixed(2)}%
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </div>
  );
}
